import {
  LogOutIcon,
  ArrowRightIcon,
  StoreIcon,
  CheckIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  CodeIcon,
  SettingsIcon,
  CreditCardIcon,
  PlusIcon,
  RocketIcon,
  RadioIcon,
  SparklesIcon,
  UsersIcon,
  ZapIcon,
} from 'lucide-react';
import * as Avatar from '@radix-ui/react-avatar';
import { tracking } from '@dashboard/lib/tracking';
import { getSellerUsage } from '@dashboard/hooks/queries/useSellerQueries';
import { sellerUsageKey } from '@dashboard/lib/queryKeys';
import { differenceInDays, endOfDay, format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useHistory,
  useLocation,
} from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useCommandPalette } from '@dashboard/components/CommandPalette';
import {
  Caret,
  NavBadge,
  NAV_ITEM_CLASS,
  NAV_SUB_ITEM_CLASS,
  NAV_SUB_LIST_CLASS,
  SidebarSearchButton,
} from './NavigationParts';
import usePrevious from '@dashboard/hooks/usePrevious';
import useSetupSteps from '@dashboard/hooks/useSetupSteps';
import { useUpdateNotification } from '@dashboard/hooks/useUpdateNotification';
import getSafeReadableColor from '@dashboard/scenes/PageEdit/utils/getSafeReadableColor';
import { MAIN_SECTIONS } from './mainSections';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import Logo from '@dashboard/components/Logo';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@dashboard/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@dashboard/components/ui/sidebar';
import { Skeleton } from '@dashboard/components/ui/skeleton';
import storage from '@dashboard/services/storage';
import { useSwitchStore } from '@dashboard/hooks/useSwitchStore';
import { CREATE_STORE_EVENT } from '@dashboard/components/CommandPalette/registries/actions';
import getSellerDomain from '@/dashboard/services/getSellerDomain';
import ConnectStripeDialog from '@dashboard/components/ConnectStripeDialog';
import { Progress } from '@dashboard/components/ui/progress';
import Dialog from '@dashboard/components/Dialog';
import Form from '@dashboard/components/Formik/Form';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import ThemeMenu from '@dashboard/components/ThemeMenu';
import TrickOrTreat from '@dashboard/components/TrickOrTreat/TrickOrTreat';

const SETTINGS_TABS = [
  { name: 'General', href: '/settings/general' },
  { name: 'Public details', href: '/settings/public-details' },
  { name: 'Team members', href: '/settings/members' },
  { name: 'Payments', href: '/settings/payments' },
  { name: 'Cart abandonment', href: '/settings/cart-abandonment' },
  { name: 'Check-in staff', href: '/settings/check-in-staff' },
  { name: 'Checkout protection', href: '/settings/checkout-protection' },
  { name: 'Invoices', href: '/settings/invoices' },
  { name: 'Domains', href: '/settings/domains' },
  { name: 'Homepage', href: '/settings/homepage' },
  { name: 'Email', href: '/settings/email' },
  { name: 'Customer portal', href: '/settings/customer-portal' },
  { name: 'Integrations', href: '/settings/integrations' },
  { name: 'Tax', href: '/settings/tax' },
  { name: 'Plan', href: '/settings/plan' },
  { name: 'Billing', href: '/settings/billing' },
  { name: 'Stripe & payouts', href: '/settings/stripe' },
  { name: 'API keys', href: '/settings/api' },
  { name: 'Webhooks', href: '/settings/webhooks' },
];

// Compact setup nudge pinned in the sidebar footer. The header routes to the
// full checklist at /setup; the button surfaces the next incomplete step.
// Connecting Stripe is the payment gate, so while it's incomplete the button
// opens the connect dialog directly instead of routing to the checklist.
function SetupCallout() {
  const { steps, completedCount, totalCount, allCompleted, isLoading } = useSetupSteps();

  if (isLoading || allCompleted) return null;

  const stripeStep = steps.find(step => step.link === '/settings/stripe');
  const needsStripe = stripeStep && !stripeStep.completed;
  const nextStep = steps.find(step => !step.completed && !step.loading && !step.optional);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-sidebar-border bg-white dark:bg-background p-3">
      <RouterLink to="/setup" className="flex items-center gap-2">
        <span className="text-[13px] font-semibold">Finish setup</span>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {completedCount} of {totalCount}
        </span>
        <ChevronRightIcon className="h-3 w-3 text-muted-foreground" />
      </RouterLink>
      <Progress value={totalCount > 0 ? (completedCount / totalCount) * 100 : 0} />
      {needsStripe ? (
        <ConnectStripeDialog
          trigger={
            <ShadcnButton size="lg" className="w-full justify-between">
              <span className="flex items-center gap-1.5">
                <CreditCardIcon className="h-4 w-4" />
                Connect Stripe
              </span>
              <ArrowRightIcon className="h-4 w-4" />
            </ShadcnButton>
          }
        />
      ) : (
        nextStep && (
          <ShadcnButton href="/setup" size="lg" className="w-full justify-between">
            <span className="truncate">{nextStep.title}</span>
            <ArrowRightIcon className="h-4 w-4" />
          </ShadcnButton>
        )
      )}
    </div>
  );
}

const NAV_GROUP_STORAGE_KEY = 'nav-group-open';

function readGroupOpenState() {
  try {
    const stored = storage.local.getItem(NAV_GROUP_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored) || {};
  } catch {
    return {};
  }
}

function writeGroupOpenState(next) {
  try {
    storage.local.setItem(NAV_GROUP_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore storage failures (quota, privacy mode, etc.)
  }
}

function useGroupOpen(key, { defaultOpen = true } = {}) {
  const [open, setOpen] = useState(() => {
    const stored = readGroupOpenState();
    return stored[key] ?? defaultOpen;
  });

  const onOpenChange = useCallback(
    nextOpen => {
      setOpen(nextOpen);
      const stored = readGroupOpenState();
      writeGroupOpenState({ ...stored, [key]: nextOpen });
    },
    [key],
  );

  return [open, onOpenChange];
}

function isInScope(pathname, scopes) {
  return scopes.some(scope => pathname === scope || pathname.startsWith(`${scope}/`));
}

// In the rail's slide-out each section is a closed link to its first page.
const SectionsAsLinksContext = createContext(false);

function SectionLink({ to, label, Icon, isActive }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} className={NAV_ITEM_CLASS}>
        <RouterNavLink to={to}>
          <Icon />
          <span>{label}</span>
          <Caret isOpen={false} />
        </RouterNavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function PageTypeCollapsible({
  groupKey,
  to,
  scopes = [to],
  label,
  listLabel,
  badge,
  Icon,
  pathname,
  children,
}) {
  const [isOpen, setOpen] = useGroupOpen(groupKey, { defaultOpen: false });
  const inScope = isInScope(pathname, scopes);
  const wasInScope = usePrevious(inScope);

  useEffect(() => {
    if (inScope && !wasInScope && !isOpen) {
      setOpen(true);
    }
  }, [inScope, wasInScope, isOpen, setOpen]);

  if (useContext(SectionsAsLinksContext)) {
    return <SectionLink to={to} label={label} Icon={Icon} isActive={inScope} />;
  }

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton aria-expanded={isOpen} className={NAV_ITEM_CLASS}>
            <Icon />
            <span>{label}</span>
            <NavBadge>{badge}</NavBadge>
            <Caret isOpen={isOpen} />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className={NAV_SUB_LIST_CLASS}>
            <SubNavItem to={to} exact pathname={pathname}>
              {listLabel ?? label}
            </SubNavItem>
            {children}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function SubNavItem({ to, pathname, exact = false, children }) {
  const isActive = exact ? pathname === to || pathname === `${to}/` : pathname.startsWith(to);
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive} className={NAV_SUB_ITEM_CLASS}>
        <RouterNavLink to={to}>{children}</RouterNavLink>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}

const SETTINGS_SCOPES = ['/settings'];

function StoreSettingsCollapsible({ pathname }) {
  const inScope = isInScope(pathname, SETTINGS_SCOPES);
  const [isOpen, setOpen] = useState(inScope);
  const wasInScope = usePrevious(inScope);

  useEffect(() => {
    if (inScope && !wasInScope && !isOpen) {
      setOpen(true);
    }
  }, [inScope, wasInScope, isOpen]);

  if (useContext(SectionsAsLinksContext)) {
    return (
      <SectionLink
        to={SETTINGS_TABS[0].href}
        label="Store settings"
        Icon={SettingsIcon}
        isActive={inScope}
      />
    );
  }

  return (
    <Collapsible asChild open={isOpen} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton aria-expanded={isOpen} className={NAV_ITEM_CLASS}>
            <SettingsIcon />
            <span>Store settings</span>
            <Caret isOpen={isOpen} />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub className={NAV_SUB_LIST_CLASS}>
            {SETTINGS_TABS.map(tab => (
              <SidebarMenuSubItem key={tab.href}>
                <SidebarMenuSubButton
                  asChild
                  isActive={pathname === tab.href}
                  className={NAV_SUB_ITEM_CLASS}
                >
                  <RouterNavLink to={tab.href}>{tab.name}</RouterNavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function formatUsd(cents) {
  return `$${Math.round((cents || 0) / 100).toLocaleString('en-US')}`;
}

function UsageMeter({ heading, subline, footnote, used, limit, overLimitText, capped }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = capped !== undefined ? capped : limit > 0 && used >= limit;
  const detail = [`${formatUsd(used)} of ${formatUsd(limit)}`, subline || footnote]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="flex flex-col gap-1.5 px-2 py-1">
      <div className="flex items-center">
        <span className="text-[13px] font-semibold">{heading}</span>
        <RouterLink
          to="/upgrade?upgradeFlow=sidebar"
          className="ml-auto text-xs font-semibold underline underline-offset-2"
        >
          Upgrade
        </RouterLink>
      </div>
      <Progress value={pct} />
      <p className="text-[11px] text-muted-foreground">{detail}</p>
      {over && overLimitText && <p className="text-xs text-muted-foreground">{overLimitText}</p>}
      {over && (
        <ShadcnButton href="/upgrade?upgradeFlow=sidebar" size="lg" className="w-full mt-1">
          <ZapIcon className="w-4 h-4 fill-current" /> Upgrade
        </ShadcnButton>
      )}
    </div>
  );
}

// The main dashboard's sections, shared by the main sidebar and the rail beside a page's sidebar.
export function MainNavItems({ pathname, sectionsAsLinks = false }) {
  const { isSeatLocked, shouldShowNavigation } = useMainNavAccess();
  const docs = MAIN_SECTIONS.find(section => section.key === 'docs');

  return (
    <SectionsAsLinksContext.Provider value={sectionsAsLinks}>
      {shouldShowNavigation &&
        MAIN_SECTIONS.filter(section => section.key !== 'settings' && section.key !== 'docs').map(
          section =>
            section.subItems ? (
              <PageTypeCollapsible
                key={section.key}
                groupKey={section.key}
                to={section.to}
                scopes={[section.to, ...section.subItems.map(item => item.to)]}
                label={section.label}
                listLabel={section.listLabel}
                badge={section.badge}
                Icon={section.Icon}
                pathname={pathname}
              >
                {section.subItems.map(item => (
                  <SubNavItem key={item.to} to={item.to} pathname={pathname}>
                    {item.label}
                  </SubNavItem>
                ))}
              </PageTypeCollapsible>
            ) : (
              <SidebarMenuItem key={section.key}>
                <SidebarMenuButton
                  asChild
                  isActive={
                    section.exact ? pathname === section.to : pathname.startsWith(section.to)
                  }
                  className={NAV_ITEM_CLASS}
                >
                  <RouterNavLink to={section.to} exact={section.exact}>
                    <section.Icon />
                    <span>{section.label}</span>
                    <NavBadge>{section.badge}</NavBadge>
                  </RouterNavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ),
        )}
      {!isSeatLocked && <StoreSettingsCollapsible pathname={pathname} />}

      <SidebarMenuItem>
        <SidebarMenuButton asChild className={NAV_ITEM_CLASS}>
          <a href={docs.href} target="_blank" rel="noreferrer">
            <docs.Icon />
            <span>{docs.label}</span>
          </a>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SectionsAsLinksContext.Provider>
  );
}

export function useMainNavAccess() {
  const {
    isAdminLoggedInAsSeller,
    isTrialing,
    hasActiveSubscription,
    isAuthenticating,
    isFree,
    isOwner,
    hasPastDueSubscription,
  } = useAuth();
  // A non-owner member of a Free workspace is locked out (spec 2.5.6) — lock the
  // sidebar nav like an expired subscription while the main area shows the notice.
  const isSeatLocked = isFree && !isOwner && !isAdminLoggedInAsSeller;
  const shouldShowNavigation = !(
    (isAuthenticating === false &&
      hasActiveSubscription === false &&
      !isTrialing &&
      !isFree &&
      !isAdminLoggedInAsSeller) ||
    hasPastDueSubscription ||
    isSeatLocked
  );
  return { isSeatLocked, shouldShowNavigation };
}

function Navigation({ hasTopBanner }) {
  const { pathname } = useLocation();
  const previousPathname = usePrevious(pathname);
  const { isMobile, setOpenMobile, state } = useSidebar();
  const isIconOnly = !isMobile && state === 'collapsed';
  const { openPalette } = useCommandPalette();
  const {
    seller,
    isTrialing,
    hasActiveSubscription,
    isCanceled,
    isTrialExpired,
    isFree,
    plan,
    token,
  } = useAuth();
  // Close mobile drawer when navigating to a new route.
  useEffect(() => {
    if (isMobile && previousPathname && pathname !== previousPathname) {
      setOpenMobile(false);
    }
  }, [pathname, previousPathname, isMobile, setOpenMobile]);

  const trialDaysRemaining =
    seller.trialEnd && differenceInDays(endOfDay(new Date(seller.trialEnd)), new Date());

  const { data: usage } = useQuery({
    queryKey: sellerUsageKey(seller?._id),
    queryFn: () => getSellerUsage(token, seller._id),
    enabled: !!seller?._id && (isTrialing || isFree),
    staleTime: 60_000,
  });
  const used = usage?.used ?? 0;
  const freeResetLabel = usage?.periodEnd
    ? `resets ${format(new Date(usage.periodEnd), 'MMM d')}`
    : null;

  const { shouldShowNavigation } = useMainNavAccess();

  const isUpgradePage = pathname.includes('/upgrade') || pathname.includes('/payment-due');

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className={hasTopBanner ? 'py-0 md:pt-[var(--top-banner-h,0px)]' : 'py-0'}>
        <div className="flex h-14 items-center pl-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:pl-0">
          <RouterLink to="/" className="flex items-center text-foreground">
            <Logo size={20} />
          </RouterLink>
          <TrickOrTreat className="ml-auto mr-1 group-data-[collapsible=icon]:hidden" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* px-3 centres the icons when the sidebar is down to icons. */}
        <SidebarGroup className="pt-4 group-data-[collapsible=icon]:px-3">
          <SidebarMenu>
            {shouldShowNavigation && isMobile && (
              <SidebarMenuItem className="mb-1">
                <SidebarSearchButton
                  onClick={() => {
                    setOpenMobile(false);
                    openPalette('sidebar_click');
                  }}
                />
              </SidebarMenuItem>
            )}

            <MainNavItems pathname={pathname} sectionsAsLinks={isIconOnly} />
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="group-data-[collapsible=icon]:px-3">
        <div className="flex flex-col gap-2 group-data-[collapsible=icon]:hidden">
          {shouldShowNavigation && !isUpgradePage && <SetupCallout />}

          {!isUpgradePage && isTrialing && (
            <UsageMeter
              heading="Free trial (Scale)"
              subline={`${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} left`}
              used={used}
              limit={usage?.limit ?? 300000}
              overLimitText="You've reached the $3K trial limit and your pages are paused. Upgrade now to keep your store active."
              capped={usage?.capReached}
            />
          )}
          {!isUpgradePage && isFree && (
            <UsageMeter
              heading="Free plan"
              footnote={freeResetLabel}
              used={used}
              limit={usage?.limit ?? 300000}
              overLimitText="You've reached the $3K sales limit and your pages are paused. Upgrade to start selling again."
              capped={usage?.capReached}
            />
          )}

          {!isUpgradePage && seller._id && !hasActiveSubscription && !isTrialing && !isFree && (
            <div className="border border-gray-100 dark:border-border rounded-md p-3 bg-white dark:bg-background">
              {isTrialExpired && (
                <>
                  <h3>Free trial</h3>
                  <p className="text-sm text-gray-500 dark:text-muted-foreground">
                    Your trial expired
                  </p>
                </>
              )}

              {isCanceled && (
                <>
                  <h3>{plan.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-muted-foreground">
                    Your subscription ended
                  </p>
                </>
              )}
              <ShadcnButton href="/upgrade" size="lg" className="w-full mt-2">
                <ZapIcon className="w-4 h-4 fill-current" /> Upgrade
              </ShadcnButton>
            </div>
          )}
        </div>

        <Profile />
      </SidebarFooter>
    </Sidebar>
  );
}

export function Profile() {
  const { user, seller, logout, isAdmin, isAuthenticating, availableStores, activeSeller } =
    useAuth();
  const { markUpdatesAsSeen } = useUpdateNotification();
  const { switchStore, createStore, isSwitching } = useSwitchStore();
  const [isCreateStoreDialogOpen, setIsCreateStoreDialogOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const handler = () => setIsCreateStoreDialogOpen(true);
    window.addEventListener(CREATE_STORE_EVENT, handler);
    return () => window.removeEventListener(CREATE_STORE_EVENT, handler);
  }, []);

  const { isMobile } = useSidebar();

  if (isAuthenticating) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex h-12 items-center gap-2 rounded-md p-2">
            <Skeleton className="h-8 w-8 flex-shrink-0 rounded-lg" />
            <div className="flex flex-1 flex-col gap-1.5">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  const width = 32;
  const height = 32;
  const storeName = seller.displayName || seller.name || user.name || 'Untitled';

  const logo = getImageSrcAndSrcSet({
    url: seller.logo?.url,
    width,
    height,
  });

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                aria-label={`Account menu for ${storeName}`}
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar.Root
                  key={seller._id}
                  className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg dark:ring-1 dark:ring-foreground/25"
                >
                  {logo ? (
                    <Avatar.Image
                      src={logo.src}
                      srcSet={logo.srcSet}
                      alt={seller.name}
                      className="h-full w-full object-cover"
                      width={width}
                      height={height}
                    />
                  ) : (
                    <Avatar.Fallback
                      className="w-full h-full flex uppercase items-center justify-center font-bold"
                      style={{
                        background: seller.brandColor,
                        color: getSafeReadableColor(seller.brandColor),
                      }}
                    >
                      {seller.displayName?.charAt(0)}
                    </Avatar.Fallback>
                  )}
                </Avatar.Root>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{storeName}</span>
                  {user?.email && (
                    <span className="truncate text-xs text-sidebar-foreground/70">
                      {user.email}
                    </span>
                  )}
                </div>
                <ChevronsUpDownIcon className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
              className="w-56 rounded-lg"
            >
              {availableStores?.map(store => {
                if (store.disabled) return null;
                const isActive = store._id === activeSeller?._id;
                const storeLogo = getImageSrcAndSrcSet({
                  url: store.logo?.url,
                  width: 24,
                  height: 24,
                });
                return (
                  <DropdownMenuItem
                    key={store._id}
                    onSelect={() => !isActive && switchStore(store._id)}
                    disabled={isSwitching}
                    className="gap-2.5"
                  >
                    <Avatar.Root className="h-6 w-6 flex-shrink-0 overflow-hidden rounded-md dark:ring-1 dark:ring-foreground/25">
                      {storeLogo ? (
                        <Avatar.Image
                          src={storeLogo.src}
                          srcSet={storeLogo.srcSet}
                          alt={store.displayName || store.name}
                          className="h-full w-full object-cover"
                          width={24}
                          height={24}
                        />
                      ) : (
                        <Avatar.Fallback
                          className="w-full h-full flex uppercase items-center justify-center text-xs font-bold"
                          style={{
                            background: store.brandColor || '#e5e7eb',
                            color: getSafeReadableColor(store.brandColor || '#e5e7eb'),
                          }}
                        >
                          {(store.displayName || store.name)?.charAt(0)}
                        </Avatar.Fallback>
                      )}
                    </Avatar.Root>
                    <span className="flex-1 truncate">{store.displayName || store.name}</span>
                    {isActive && (
                      <CheckIcon className="h-4 w-4 text-black dark:text-foreground ml-auto" />
                    )}
                  </DropdownMenuItem>
                );
              })}

              <DropdownMenuItem
                onSelect={() => {
                  tracking.trackEvent('create_store_clicked');
                  setIsCreateStoreDialogOpen(true);
                }}
                className="gap-2.5"
              >
                <div className="h-6 w-6 flex-shrink-0 flex items-center justify-center rounded-md bg-gray-100 dark:bg-muted">
                  <PlusIcon className="h-3.5 w-3.5 text-gray-500 dark:text-muted-foreground" />
                </div>
                <span>Create new store</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onSelect={() => window.open(`${getSellerDomain(seller)}/portal`, '_blank')}
              >
                <StoreIcon className="h-5 w-5" />
                <span>Customer portal</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onSelect={() => {
                  markUpdatesAsSeen();
                  history.push('/updates');
                }}
              >
                <SparklesIcon className="h-5 w-5" />
                <span>What&apos;s new</span>
              </DropdownMenuItem>

              <DropdownMenuItem onSelect={() => window.open('/docs/api', '_blank')}>
                <CodeIcon className="h-5 w-5" />
                <span>API reference</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onSelect={() => window.open('https://status.checkoutpage.com', '_blank')}
              >
                <RadioIcon className="h-5 w-5" />
                <span>System status</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onSelect={() => window.open('/affiliate-program', '_blank')}>
                <RocketIcon className="h-5 w-5" />
                <span>Affiliate program</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              <DropdownMenuItem onSelect={() => history.push('/account/profile')}>
                <UsersIcon className="h-5 w-5" />
                <span>Account</span>
              </DropdownMenuItem>

              <ThemeMenu />

              {isAdmin && (
                <DropdownMenuItem onSelect={() => history.push('/admin')}>
                  <SettingsIcon className="h-5 w-5" />
                  <span>Admin</span>
                </DropdownMenuItem>
              )}

              <DropdownMenuItem onSelect={logout}>
                <LogOutIcon className="h-5 w-5" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <Dialog
        open={isCreateStoreDialogOpen}
        onCancel={() => setIsCreateStoreDialogOpen(false)}
        title="Create new store"
        cancelButton={null}
        confirmButton={null}
      >
        <Form
          showState={false}
          initialValues={{ displayName: '' }}
          onSubmit={async values => {
            try {
              await createStore({ displayName: values.displayName });
              setIsCreateStoreDialogOpen(false);
              return { data: values };
            } catch (error) {
              return { error: error.message };
            }
          }}
          fields={[
            {
              name: 'displayName',
              label: 'Store name',
              type: 'text',
              placeholder: 'e.g. My Store',
              required: true,
            },
          ]}
          submitButton={{
            label: 'Create store',
            size: 'full',
          }}
        />
      </Dialog>
    </>
  );
}

export default Navigation;
